import { TrendingDown, UserMinus, DollarSign, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../translations";
import { useRefonte } from "../translations/refonte";
import { useCountUp } from "../hooks/useCountUp";
import { CayusWave } from "./CayusWave";

const AnimatedStat = ({ value, delay, glowClass, children }) => {
  const { ref, displayValue } = useCountUp(value, { duration: 2200, delay });
  return (
    <div ref={ref}>
      <p className={`text-4xl sm:text-5xl font-bold text-white mb-2 tabular-nums ${glowClass}`} data-testid="animated-counter">
        <span className="stat-sweep">{displayValue}</span>
      </p>
      {children}
    </div>
  );
};

const CostSection = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const r = useRefonte(language);
  const c = r.cost;

  const icons = [<UserMinus className="w-6 h-6" />, <TrendingDown className="w-6 h-6" />, <DollarSign className="w-6 h-6" />];
  const colors = [
    "text-[#F87171] bg-red-500/10 border-red-500/20",
    "text-[#FBBF24] bg-amber-500/10 border-amber-500/20",
    "text-[#34D399] bg-emerald-500/10 border-emerald-500/20",
  ];
  const glows = ["roi-glow-red", "roi-glow-amber", "roi-glow-emerald"];

  return (
    <section className="roi-dark-bg py-16 lg:py-20 relative overflow-hidden" data-testid="roi-section">
      <div className="absolute inset-0 dots-drift pointer-events-none opacity-70" aria-hidden="true" />
      <div className="wrap-cayus relative">
        <div className="max-w-3xl mb-10">
          <div className="rule-cobalt mb-5 !bg-[linear-gradient(90deg,#5EB3F6_0%,#0047AB_100%)]" />
          <p className="mono-cayus text-[#5EB3F6] mb-4">{c.eyebrow}</p>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl text-white tracking-tight mb-4">{c.title}</h2>
          <p className="font-body-cayus text-lg text-[#E4EEFC] leading-relaxed">{c.lead}</p>
        </div>

        {/* Les 3 constats */}
        <div className="grid md:grid-cols-3 gap-5 mb-12">
          {c.facts.map((f, i) => (
            <div key={i} className="bg-white/[0.04] border border-white/10 rounded-xl p-6 hover:border-[#5EB3F6]/40 transition-colors" data-testid={`cost-fact-${i}`}>
              <span className="mono-cayus text-[#5EB3F6]">0{i + 1}</span>
              <p className="font-body-cayus text-slate-200 leading-relaxed mt-3">{f}</p>
            </div>
          ))}
        </div>

        {/* La frise du décalage */}
        <p className="mono-cayus text-[11px] text-[#9098A3] mb-4">{c.timelineLabel}</p>
        <div className="flex flex-col md:flex-row items-stretch gap-2 mb-14" data-testid="blindspot-timeline">
          {c.timeline.map((step, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div
                className={`flex-1 rounded-xl px-4 py-3 text-sm font-body-cayus border ${
                  i === c.timeline.length - 1
                    ? "border-[#F59E0B]/60 bg-[#F59E0B]/[0.12] text-[#FDE68A]"
                    : "border-white/10 bg-white/[0.03] text-[#CFE6FC]"
                }`}
              >
                {step}
              </div>
              {i < c.timeline.length - 1 && <ArrowRight className="w-4 h-4 text-[#5E6C83] flex-shrink-0 hidden md:block" />}
            </div>
          ))}
        </div>

        {/* Les chiffres */}
        <div className="grid sm:grid-cols-3 gap-5 mb-10" data-testid="roi-stats">
          {t.roi.stats.map((stat, i) => (
            <div
              key={i}
              className="bg-white/[0.04] border border-white/10 rounded-xl p-7 text-center backdrop-blur-sm"
              data-testid={`roi-stat-${i}`}
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border mb-4 ${colors[i]}`}>{icons[i]}</div>
              <AnimatedStat value={stat.value} delay={i * 300} glowClass={glows[i]}>
                <p className="text-sm text-[#E4EEFC] leading-relaxed">{stat.label}</p>
                <p className="text-xs text-[#5E6C83] mt-2">{stat.source}</p>
              </AnimatedStat>
            </div>
          ))}
        </div>

        <div className="bg-white/[0.04] border border-[#10B981]/25 rounded-xl p-6 sm:p-8 text-center backdrop-blur-sm" data-testid="roi-callout">
          <p className="text-[#34D399] text-xl sm:text-2xl font-bold mb-2 roi-glow-emerald">{t.roi.savings}</p>
          <p className="text-[#9098A3] text-sm max-w-xl mx-auto">{t.roi.savingsNote}</p>
        </div>

        <p className="pull-quote text-center text-[#CFE6FC] text-xl mt-10 max-w-3xl mx-auto">« {c.quote} »</p>
      </div>
      <CayusWave className="w-full h-10 mt-12" opacity={0.3} />
    </section>
  );
};

export default CostSection;
